import { NestedValue } from 'react-hook-form';

import { PatientFormTemplate } from '@lib/resources/types';

import { JumpLogic } from './components/CustomFormBuilder/components/JumpLogicV2/types';
import { FormAllRequiredQuestionsKeys, PageStatus } from './utils';

export enum EChecklistItemCategory {
  Intake = 'intake',
  Demographics = 'demographics',
  Consent = 'consent',
  Insurance = 'insurance',
  Payment = 'payment',
}

export enum FormQuestionTypeKeys {
  address = 'address',
  copay = 'copay',
  date = 'date',
  docusign = 'docusign_templates',
  dropdown = 'dropdown',
  eSignature = 'e_signature',
  email = 'email',
  group = 'group',
  imh = 'imh',
  informationCapture = 'information_capture',
  insuranceCapture = 'insurance_capture',
  insuranceCard = 'insurance_card',
  jump = 'jump',
  longText = 'long_text',
  medicationSearch = 'medication_search',
  multipleChoice = 'multiple_choice',
  patientInformation = 'patient_information',
  pharmacySearch = 'pharmacy_search',
  phoneNumber = 'phone_number',
  scheduler = 'scheduler',
  shortText = 'short_text',
  ssn = 'ssn',
  template = 'template',
  twoFactor = '2FA',
  yesNo = 'yes_no',
  zipcode = 'zipcode',
  // CCDA related questions types
  allergy = 'allergy',
  demographics = 'demographics',
  familyHistory = 'family_history',
  immunizationHistory = 'immunization_history',
  medicationList = 'medication_list',
  socialHistory = 'social_history',
  surgicalHistory = 'surgical_history',
  resources = 'resources',
}

export type TList = {
  id: string;
  name: string;
};

export type TSchedulerFilters = {
  appointmentType: string[];
  facility: string[];
  provider: string[];
};

export type TDocusignTemplate = {
  templateId: string;
  name: string;
};

// form builder reducer state type
export type TFormBuilderState = {
  status: PageStatus;
  data: false | PatientFormTemplate | PatientFormTemplate[] | TCustomFormValues;
  widgetUser: string;
};

// question configuration entries type
export type TQuestionTypeConfigurationOptions = {
  canBeFinal: boolean;
  canSelectAnswers: boolean;
  disabled: boolean;
  icon: string;
  isAvailableOnMenu: boolean;
  isCCDA: boolean;
  isFinal: boolean;
  isLumaIcon: boolean;
  isText: boolean;
  label: string;
  showCanSkip: boolean;
  showDescriptionField: boolean;
  showImportAnswersButton: boolean;
  showPreviewButton: boolean;
  showRedirectContent: Boolean;
  showRequiredButton: boolean;
  showScript: boolean;
  showSettingsButton: boolean;
  showSquigglyButton: boolean;
  supportsSquiggly: boolean;
  titlePlaceholder: string;
};

export type TRedirectConfigurationReschedule = {
  appointmentType?: Array<string>;
  facility?: Array<string>;
  provider?: Array<string>;
};

export type THistoryQuestionValues = {
  id: string;
  code?: string;
  codeSystem?: string;
  description?: string;
  friendlyName?: string;
  friendlyNameSpanish?: string;
  friendlyNamePortuguese?: string;
  showFriendlyName?: boolean;
};

export type TIMHQuestionValues = {
  id: string;
  code?: string;
  description?: string;
};

export type THistoryQuestionSearchOption = {
  _id: string;
  code: string;
  codeSystemRef?: string;
  description: string;
  valueSetRef?: string;
};

export type TResourceType = 'facility' | 'provider' | 'appointmentType';

export type TQueueSquigglyMapping = {
  [squiggly: string]: string;
};

export type TFormAnswerValues = {
  allowResourcesSearching: boolean;
  code: string;
  disabled: boolean;
  hideCalendar: boolean;
  id: string;
  label: string;
  labelPortuguese: string;
  labelSpanish: string;
  redirectType: string;
  redirectValue: string | TRedirectConfigurationReschedule;
  queueSquigglyMapping: TQueueSquigglyMapping[];
  score: string;
  showFilters: boolean;
  showZipcodeFilter: boolean;
  useExternalResources: boolean;
  useFilterFromResourcesQuestions: boolean;
  zipcodeMaxDistance: number;
};

export enum TInsuranceCardOptions {
  DEFAULT = 'DEFAULT',
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
}

export enum EInsuranceCaptureMethod {
  MANUAL = 'manual',
  SPARK = 'spark',
}

export type TResourceQuestionFilterParams = {
  facility: string[];
  appointmentType: string[];
  provider: string[];
};

export type TResourceQuestionGenericFilterParams = {
  facility?: {
    ids?: string[];
    [key: string]: any;
  };
  appointmentType?: {
    ids?: string[];
    [key: string]: any;
  };
  provider?: {
    ids?: string[];
    [key: string]: any;
  };
};

// define the custom form fields types to be used in the form component on add/edit page
// this type is used by react-hook-form to manage the form and should reflect the form view not the backend.
export type TFormQuestionValues = {
  actionLabel: string;
  actionLabelPortuguese: string;
  actionLabelSpanish: string;
  actionLink: string;
  actionLinkPortuguese: string;
  actionLinkSpanish: string;
  addToAppointment: boolean;
  allowMultipleSelection: boolean;
  allowOtherChoice: boolean;
  allowResourcesSearching: boolean;
  answers: NestedValue<TFormAnswerValues[]> | NestedValue<[]>;
  canSkip: boolean;
  copay: string;
  chargePatientBalance?: boolean;
  displayRadioButton?: boolean;
  description: string;
  descriptionPortuguese: string;
  descriptionSpanish: string;
  docusign?: string;
  familyHistory: NestedValue<THistoryQuestionValues[]> | NestedValue<[]>;
  hideCalendar: boolean;
  id: string;
  imhQuestionnaires: TIMHQuestionValues[];
  immunizationHistory: NestedValue<THistoryQuestionValues[]> | NestedValue<[]>;
  includeInsuranceCard: TInsuranceCardOptions;
  insuranceCaptureMethod: EInsuranceCaptureMethod;
  runInsuranceEligibility?: boolean;
  onlyPullInsuranceEHR?: boolean;
  writebackPDFToEhrOnlyWhenInsuranceIsAltered?: boolean;
  validationMessageRequired?: string;
  insuranceEligibilityFailed?: string;
  insuranceEligibilityNotEligible?: string;
  promptInsuranceDeletion?: boolean;
  isPatientSignature?: boolean;
  allowSaveSignature?: boolean;
  showSignatureDisclaimer?: boolean;
  signatureDisclaimerText?: string;
  promptInsuranceDeletionMessage?: string;
  lumaPayCopayUnknown?: string;
  includeNoneOfAboveChoice: boolean;
  filterAvailabilityBasedOnZipcode: boolean;
  isDefaultCurrentDate: boolean;
  mappingField: string;
  mappingFieldIndicator?: string;
  needsReview?: boolean;
  mustExist: boolean;
  persistAnswer: boolean;
  redirectType: string;
  redirectValue: string | TRedirectConfigurationReschedule;
  queueSquigglyMapping: TQueueSquigglyMapping[];
  required: boolean;
  resourceType?: TResourceType;
  resourceFilterParams?: TResourceQuestionFilterParams;
  resourceGenericFilterParams?: TResourceQuestionGenericFilterParams;
  schedulerFilters: TSchedulerFilters;
  schedulerHideCalendar: boolean;
  schedulerAllowResourcesSearching: boolean;
  schedulerShowFilters: boolean;
  schedulerShowZipcodeFilter: boolean;
  schedulerZipcodeMaxDistance: number;
  schedulerBookOnFormComplete: boolean;
  schedulerUseExternalResources: boolean;
  schedulerLockTimeout: number;
  schedulerRequired: boolean;
  schedulerAnswerId: string;
  showAnswerCodeField: boolean;
  scoringEnabled: boolean;
  script: string;
  showCustomAction: boolean;
  showDescription: boolean;
  showFilters: boolean;
  showZipcodeFilter: boolean;
  socialHistory: NestedValue<TFormSubQuestionValues[]> | NestedValue<[]>;
  squiggly: string;
  subQuestions: NestedValue<TFormSubQuestionValues[]> | NestedValue<[]>;
  surgicalHistory: NestedValue<THistoryQuestionValues[]> | NestedValue<[]>;
  template: string;
  templatePortuguese: string;
  templateSpanish: string;
  text: string;
  title: string;
  titlePortuguese: string;
  titleSpanish: string;
  type: string;
  useExternalResources: boolean;
  useFilterFromResourcesQuestions: boolean;
  visibility: string;
  zipcodeMaxDistance: number;
  allergyReactionRequired?: boolean;
  allergySeverityRequired?: boolean;
  showEstimatedPayment: boolean;
  estimatedPaymentMessageForOneCopay?: string;
  estimatedPaymentMessageForMultipleCopays?: string;
};

export type TFormSubQuestionValues = {
  actionLabel: string;
  actionLabelPortuguese: string;
  actionLabelSpanish: string;
  actionLink: string;
  actionLinkPortuguese: string;
  actionLinkSpanish: string;
  addToAppointment: boolean;
  allowMultipleSelection: boolean;
  allowOtherChoice: boolean;
  allowResourcesSearching: boolean;
  answers: NestedValue<TFormAnswerValues[]> | NestedValue<[]>;
  canSkip: boolean;
  copay: string;
  chargePatientBalance: boolean;
  displayRadioButton?: boolean;
  description: string;
  descriptionPortuguese: string;
  descriptionSpanish: string;
  docusign?: string;
  filterAvailabilityBasedOnZipcode: boolean;
  hideCalendar: boolean;
  id: string;
  needsReview?: boolean;
  includeInsuranceCard: TInsuranceCardOptions;
  insuranceCaptureMethod: EInsuranceCaptureMethod;
  runInsuranceEligibility: boolean;
  onlyPullInsuranceEHR: boolean;
  writebackPDFToEhrOnlyWhenInsuranceIsAltered: boolean;
  validationMessageRequired?: string;
  insuranceEligibilityFailed?: string;
  insuranceEligibilityNotEligible?: string;
  lumaPayCopayUnknown?: string;
  promptInsuranceDeletion?: boolean;
  isPatientSignature?: boolean;
  allowSaveSignature?: boolean;
  showSignatureDisclaimer?: boolean;
  signatureDisclaimerText?: string;
  promptInsuranceDeletionMessage?: string;
  includeNoneOfAboveChoice: boolean;
  isDefaultCurrentDate: boolean;
  mappingField: string;
  mappingFieldIndicator?: string;
  mustExist: boolean;
  persistAnswer: boolean;
  redirectType: string;
  redirectValue: string | TRedirectConfigurationReschedule;
  queueSquigglyMapping: TQueueSquigglyMapping[];
  required: boolean;
  resourceType?: TResourceType;
  resourceFilterParams?: TResourceQuestionFilterParams;
  resourceGenericFilterParams?: TResourceQuestionGenericFilterParams;
  scoringEnabled: boolean;
  script: string;
  showAnswerCodeField: boolean;
  showCustomAction: boolean;
  showDescription: boolean;
  showFilters: boolean;
  showZipcodeFilter: boolean;
  squiggly: string;
  template: string;
  templatePortuguese: string;
  templateSpanish: string;
  text: string;
  title: string;
  titlePortuguese: string;
  titleSpanish: string;
  type: string;
  useExternalResources: boolean;
  useFilterFromResourcesQuestions: boolean;
  visibility: string;
  zipcodeMaxDistance: number;
  allergyReactionRequired: boolean;
  allergySeverityRequired: boolean;
  showEstimatedPayment: boolean;
  estimatedPaymentMessageForOneCopay?: string;
  estimatedPaymentMessageForMultipleCopays?: string;
};

// define the custom form fields types to be used in the form component on add/edit page
// this type is used by react-hook-form to manage the form and should reflect the form view not the backend.
export type TCustomFormValues = {
  active: boolean;
  allRequired: FormAllRequiredQuestionsKeys;
  checklistCategory: EChecklistItemCategory;
  facilities: string;
  id: string;
  respectTemplateHTML: boolean;
  insurance: boolean;
  jumpLogic?: JumpLogic;
  language: string;
  questions: NestedValue<TFormQuestionValues[]>;
  reminder: boolean;
  sendToDirectMessageEmail: boolean;
  writebackToEhr: boolean;
  writebackPDFToEhr: boolean;
  categoryNameToWritebackPDF: string;
  skipWelcomeQuestion: boolean;
  source: string;
  templateAction: string;
  templateActionPortuguese: string;
  templateActionSpanish: string;
  templateId: string;
  templateText: string;
  templateTextPortuguese: string;
  templateTextSpanish: string;
  templateTitle: string;
  templateTitlePortuguese: string;
  templateTitleSpanish: string;
  templateWrapLabels: boolean;
  title: string;
  type: string;
  version: string;
  questionsForStaffReview: { [key: string]: boolean };
};
